import type { RouterConfig } from '@nuxt/schema'

export default <RouterConfig>{
  /**
   * scrollBehavior 関数を使用しない理由
   *
   * 1. Webview 環境での問題
   *    - 同じ画面にブラウザバックした場合、savedPosition が1回目の値になる事象が発生
   *    - ネイティブ側のキャッシュが干渉している可能性が高い
   *
   * 2. window.history.state.scroll を使用した場合の問題
   *    - scrollBehavior 内で window.history.state.scroll を使用すると、以下の想定外の挙動が発生
   *
   *    【問題の挙動】
   *    1. ページAで100pxにスクロール
   *    2. ページBへの遷移ボタン押下
   *    3. ページAで0pxに戻る（想定外の挙動）
   *    4. ページBに遷移
   *    5. 戻るボタン押下
   *    6. ページBで100pxにスクロール（想定外の挙動）
   *    7. ページAで100pxにスクロール（期待通りの挙動）
   *
   * 上記の理由により、scrollBehavior 関数は使用せず、MutationObserver での処理としています。
   */
  scrollBehavior() {
    // scrollBehaviorでは何もしない（Vue Routerのすべての自動スクロール機能を無効化）
    return false
  },
}

let isBackNavigation: boolean = false
let isRestoringScroll: boolean = false
let shouldCancelRestore: boolean = false

if (import.meta.client) {
  const getCurrentPath: () => string = () => window.location.pathname
  const getCurrentScroll: () => number = () => window.scrollY

  // ユーザーの操作を検知してスクロール復元をキャンセル
  const cancelScrollRestore: () => void = () => {
    if (isRestoringScroll) {
      shouldCancelRestore = true
    }
  }

  // ユーザー操作イベント（スマホ:タッチスクロール/タップ、PC:ホイール）
  window.addEventListener('touchmove', cancelScrollRestore, { passive: true })
  window.addEventListener('touchstart', cancelScrollRestore, { passive: true })
  window.addEventListener('wheel', cancelScrollRestore, { passive: true })

  // popstateイベントで戻る/進む操作を検出
  window.addEventListener('popstate', () => {
    isBackNavigation = true
    // 新しい戻る操作なのでリセット
    shouldCancelRestore = false
  })

  /**
   * 目標スクロール位置に到達するまでリトライする関数
   *
   * @param targetPosition - 目標スクロール位置（ピクセル）
   */
  const scrollWithRetry = (targetPosition: number) => {
    let attempts: number = 0
    const maxAttempts: number = 20

    const retry: () => void = () => {
      // ユーザー操作によるキャンセルチェック
      if (shouldCancelRestore) {
        shouldCancelRestore = false
        isRestoringScroll = false
        return
      }

      // 1. 目標位置にスクロールを試行
      window.scrollTo({
        left: 0,
        top: targetPosition,
        behavior: 'instant',
      })

      // 2. 実際の位置と目標位置の差が10px以上 → 150ms後に再試行（最大20回）
      if (
        Math.abs(getCurrentScroll() - targetPosition) > 10 &&
        attempts < maxAttempts
      ) {
        attempts++
        setTimeout(retry, 150)
      } else {
        // 3. 目標位置に到達 or 最大試行回数到達 → 完了
        // 4. 完了後500ms待機してレイアウトシフトを検知し差が5px以上なら再調整
        setTimeout(() => {
          // ユーザー操作によるキャンセルチェック
          if (shouldCancelRestore) {
            shouldCancelRestore = false
            isRestoringScroll = false
            return
          }

          if (Math.abs(getCurrentScroll() - targetPosition) > 5) {
            window.scrollTo({
              left: 0,
              top: targetPosition,
              behavior: 'instant',
            })
          }
          isRestoringScroll = false
        }, 500)
      }
    }

    retry()
  }

  /**
   * ページ遷移完了後のスクロール位置を制御する関数
   *
   * - 戻る/進む操作の場合: 保存されたスクロール位置に復元（リトライ＆レイアウトシフト対策付き）
   * - 通常遷移の場合: ページトップ(0, 0)にスクロール
   */
  const handleScrollOnPageTransition = async () => {
    if (isBackNavigation) {
      // 戻る/進む操作の場合
      isBackNavigation = false

      // window.history.state.scrollから位置を取得
      const storedPosition: number | undefined =
        window.history.state?.scroll?.top

      // 保存された位置がない場合は何もしない
      if (storedPosition === undefined) {
        return
      }

      // ブラウザの描画サイクルを待つ
      await new Promise((resolve) => requestAnimationFrame(resolve))

      isRestoringScroll = true
      scrollWithRetry(storedPosition)
    } else {
      // 通常遷移の場合: ページトップ(0, 0)にスクロール
      window.scrollTo({
        left: 0,
        top: 0,
        behavior: 'instant',
      })
    }
  }

  // 初期値: 現在のパス
  let lastPath: string = getCurrentPath()

  /**
   * MutationObserver: DOM変更を監視してページ遷移を検知
   *
   * メリット:
   * - ページ遷移が完全に完了した後に実行される
   * - 遷移元のページに影響を与えない（新しいページのDOMが表示された後に動作）
   * - scrollBehaviorのタイミング問題を回避できる
   */
  const observer: MutationObserver = new MutationObserver(() => {
    // DOM変更時の最新パス
    const currentPath: string = getCurrentPath()
    if (currentPath !== lastPath) {
      lastPath = currentPath
      handleScrollOnPageTransition()
    }
  })

  // body要素の変更を監視（子要素の追加・削除、すべての子孫要素も対象）
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}
